import { Button } from '@lifeomic/chroma-react/components/Button';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { ThumbsDown, ThumbsUp } from '@lifeomic/chromicons';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  ActionPlanFeedbackTarget,
  useActionPlanFeedback,
} from './useActionPlanFeedback';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  thanks: {
    color: theme.palette.text.hint,
    fontSize: theme.pxToRem(12),
  },
  textArea: {
    borderColor: theme.palette.divider,
    borderRadius: theme.pxToRem(4),
    borderStyle: 'solid',
    borderWidth: 1,
    fontFamily: 'inherit',
    fontSize: theme.pxToRem(14),
    minHeight: theme.pxToRem(96),
    padding: theme.pxToRem(8),
    resize: 'vertical',
    width: '100%',
  },
}));

const messages = defineMessages({
  thumbsUpLabel: {
    id: 'actionPlan.feedback.thumbsUp',
    defaultMessage: 'This is helpful',
  },
  thumbsDownLabel: {
    id: 'actionPlan.feedback.thumbsDown',
    defaultMessage: 'This needs work',
  },
  modalTitle: {
    id: 'actionPlan.feedback.modalTitle',
    defaultMessage: "What's off about this?",
  },
  commentPlaceholder: {
    id: 'actionPlan.feedback.commentPlaceholder',
    defaultMessage: 'Optional: tell Zori what to change (helps tune the plan).',
  },
  submit: {
    id: 'actionPlan.feedback.submit',
    defaultMessage: 'Submit feedback',
  },
  cancel: {
    id: 'actionPlan.feedback.cancel',
    defaultMessage: 'Cancel',
  },
  thanks: {
    id: 'actionPlan.feedback.thanks',
    defaultMessage: 'Thanks for the feedback',
  },
});

export interface ActionPlanFeedbackControlsProps {
  /** What the thumb is about: the coach narrative or a single action item. */
  target: ActionPlanFeedbackTarget;
  /**
   * The coach run that produced this plan (`"zori-pulse-coach/<task_id>"`).
   * When absent there is nothing to attribute feedback to, so the controls
   * render nothing.
   */
  sourceRunId?: string;
  /** Item title for per-item feedback; omit for coach-narrative feedback. */
  actionItemTitle?: string;
  className?: string;
}

/**
 * Thumbs-up/down feedback affordance for Zori Pulse action-plan output. A
 * thumbs-up is reported immediately; a thumbs-down opens a modal for an optional
 * comment before reporting. Renders nothing unless the `zoriPulseScan` feature
 * is on and a coach `sourceRunId` is available to attribute the feedback to.
 */
export const ActionPlanFeedbackControls: React.FC<
  ActionPlanFeedbackControlsProps
> = ({ target, sourceRunId, actionItemTitle, className }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { zoriPulseEnabled, isSubmitting, submitFeedback } =
    useActionPlanFeedback();

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);

  if (!zoriPulseEnabled || !sourceRunId) {
    return null;
  }

  if (submitted) {
    return (
      <div className={className}>
        <Text className={classes.thanks}>{formatMessage(messages.thanks)}</Text>
      </div>
    );
  }

  const handleThumbsUp = async () => {
    const scored = await submitFeedback({
      sourceRunId,
      target,
      sentiment: 'positive',
      actionItemTitle,
    });
    if (scored) {
      setSubmitted(true);
    }
  };

  const handleSubmitNegative = async () => {
    const scored = await submitFeedback({
      sourceRunId,
      target,
      sentiment: 'negative',
      actionItemTitle,
      comment,
    });
    setIsModalOpen(false);
    setComment('');
    if (scored) {
      setSubmitted(true);
    }
  };

  return (
    <div className={[classes.root, className].filter(Boolean).join(' ')}>
      <IconButton
        aria-label={formatMessage(messages.thumbsUpLabel)}
        title={formatMessage(messages.thumbsUpLabel)}
        icon={ThumbsUp}
        size={0}
        disabled={isSubmitting}
        onClick={handleThumbsUp}
      />
      <IconButton
        aria-label={formatMessage(messages.thumbsDownLabel)}
        title={formatMessage(messages.thumbsDownLabel)}
        icon={ThumbsDown}
        size={0}
        disabled={isSubmitting}
        onClick={() => setIsModalOpen(true)}
      />
      <Modal
        isOpen={isModalOpen}
        onDismiss={() => setIsModalOpen(false)}
        title={formatMessage(messages.modalTitle)}
        size={1}
        fullWidth
        actions={
          <>
            <Button variant="text" onClick={() => setIsModalOpen(false)}>
              {formatMessage(messages.cancel)}
            </Button>
            <Button
              icon={ThumbsDown}
              onClick={handleSubmitNegative}
              disabled={isSubmitting}
            >
              {formatMessage(messages.submit)}
            </Button>
          </>
        }
      >
        <textarea
          aria-label={formatMessage(messages.commentPlaceholder)}
          placeholder={formatMessage(messages.commentPlaceholder)}
          className={classes.textArea}
          value={comment}
          onChange={event => setComment(event.target.value)}
        />
      </Modal>
    </div>
  );
};

export default ActionPlanFeedbackControls;
